/**
 * Reports.
 *
 * Every method takes the same period, because every report does. `download`
 * exists separately from the JSON readers for one reason worth stating: the
 * export parameter is `export`, not `format` — DRF owns `format` for content
 * negotiation, and `?format=xlsx` returns a 404 rather than a spreadsheet.
 */

import { api } from "@/api/client";
import type { HourPoint, PaymentRow, PeriodSummary, ProductRow, TrendPoint } from "./analyticsRepository";

export interface Range {
  from?: string;
  to?: string;
  section?: string;
}

export interface ExpenseLine {
  category_id: string;
  category: string;
  amount: string;
  count: number;
}

export interface ProfitAndLoss {
  from: string;
  to: string;
  net_sales: string;
  cost_of_goods: string;
  gross_profit: string;
  expenses: ExpenseLine[];
  total_expenses: string;
  net_profit: string;
  expenses_are_branch_wide: boolean;
}

export interface CashUpRow {
  shift_id: string;
  till: string;
  cashier: string;
  opened_at: string;
  closed_at: string | null;
  opening_float: string;
  expected_cash: string | null;
  counted_cash: string | null;
  variance: string | null;
  approved_by: string;
}

function query(range: Range = {}, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries({ ...range, ...extra })) {
    if (value) params.set(key, value);
  }
  return params.toString();
}

export const reportsRepository = {
  summary: (range?: Range) =>
    api.get<{ current: PeriodSummary; previous: PeriodSummary }>(
      `/reports/summary/?${query(range)}`,
    ),

  trend: (range?: Range, groupBy: "day" | "week" | "month" = "day") =>
    api.get<TrendPoint[]>(`/reports/trend/?${query(range, { group_by: groupBy })}`),

  byHour: (range?: Range) => api.get<HourPoint[]>(`/reports/by-hour/?${query(range)}`),

  products: (range?: Range, worst = false) =>
    api.get<ProductRow[]>(`/reports/products/?${query(range, worst ? { worst: "true" } : {})}`),

  slowMovers: (range?: Range) =>
    api.get<
      {
        product_id: string;
        description: string;
        quantity_on_hand: string;
        stock_value: string;
      }[]
    >(`/reports/slow-movers/?${query(range)}`),

  paymentMix: (range?: Range) => api.get<PaymentRow[]>(`/reports/payment-mix/?${query(range)}`),

  tax: (range?: Range) =>
    api.get<{ gross_sales: string; tax_total: string; net_sales: string }>(
      `/reports/tax/?${query(range)}`,
    ),

  profitAndLoss: (range?: Range) =>
    api.get<ProfitAndLoss>(`/reports/profit-and-loss/?${query(range)}`),

  expenses: (range?: Range) =>
    api.get<{ category: string; amount: string; count: number }[]>(
      `/reports/expenses/?${query(range)}`,
    ),

  cashUp: (on: string) => api.get<CashUpRow[]>(`/reports/cash-up/?on=${on}`),

  /**
   * The same figures as a spreadsheet.
   *
   * Fetched as a blob and handed to the browser as a download rather than
   * navigating to the URL, because the request needs the Authorization header —
   * a plain link would arrive unauthenticated and bounce to the login screen.
   */
  download: async (report: string, range?: Range): Promise<Blob> =>
    api.get<Blob>(`/reports/${report}/?${query(range, { export: "xlsx" })}`, {
      responseType: "blob",
    }),
};
